import Layout from './Layout';
import { Box, Grid, Typography, CircularProgress } from '@mui/material';
import QRCodeSvg from 'qrcode.react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { useFacilityPatients } from '@/hooks/useFacilityPatients';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { MainUrls } from '../constants/urls';
import { FacilityResponseType } from '@/features/reservation/types/FacilityResponseType';
import { executeGet } from '@/client/rest';

export function QRPage(props: any) {
  const [qrCodeValue, setQrCodeValue] = useState<string | null | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const { getFacilityPatientsModel } = useFacilityPatients();

  // クエリパラメータから施設IDを取得
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fid = queryParams.get('fid');
  const [facilityId] = useState<string>(fid || '');
  const [facilityName, setFacilityName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    // 2025/1/11以降 QRコードの部分は、セッションストレージを参照しなくなり、
    // クエリストリングから施設IDを取得&施設名検索して表示するようにしています
    function backToFacilityListPage() {
      const redirectUrl = MainUrls.QRCode;
      const encodedRedirectUrl = encodeURIComponent(redirectUrl);
      navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
    }

    if (facilityId === null) {
      // 施設IDがない場合は、QRコード手前の施設一覧にリダイレクト
      backToFacilityListPage();
      return;
    }

    const handleSetFacilityName = async () => {
      const facilityList = await executeGet<FacilityResponseType>({
        apiName: 'medirecoWebApi',
        path: '/get-facility-list'
      });
      const facility = facilityList.facilities?.find((facility) => facility.id === facilityId);
      if (!facility) {
        // 施設一覧が取得できない場合は施設一覧に遷移
        backToFacilityListPage();
        return;
      }
      const facilityNameResponse = facility.facility_name;

      if (facilityNameResponse === undefined) {
        // クエリパラメータにある施設IDが不適切なもの(例:0000)である場合、NotFoundページへ遷移
        navigate('/notFound');
        return;
      }
      setFacilityName(facilityNameResponse);
    };

    // クエリパラメータにある施設IDから施設名を取得してセットする
    handleSetFacilityName();
    setIsLoading(false);
  }, [location.search, navigate]);

  useEffect(() => {
    if (paused) return;

    const timerId = setInterval(() => {
      try {
        fetchUserAttributes().then(async (atr) => {
          try {
            const cognitoId = atr.sub!;
            if (facilityId === null) {
              setQrCodeValue(cognitoId);
            } else {
              try {
                const facilityPatient = await getFacilityPatientsModel(cognitoId ?? '', facilityId);
                const karteNo = facilityPatient?.karteNo;
                if (karteNo) {
                  setQrCodeValue(karteNo);
                } else {
                  setQrCodeValue(cognitoId);
                }
              } catch (e) {
                setQrCodeValue(cognitoId);
              }
            }
          } catch (error) {
            setQrCodeValue(null);
          }
        });
        setPaused(true);
      } catch (error) {
        setQrCodeValue(null);
      }
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [paused]);

  return (
    <>
      <Layout title={facilityName} backButton handleBack={() => navigate(-1)}>
        <Box sx={{ p: 2 }}>
          <Grid
            width={'100%'}
            container
            direction={'column'}
            spacing={2}
            position={'absolute'}
            mt={0}
            sx={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <Grid>
              {qrCodeValue && isLoading === false ? (
                <QRCodeSvg value={qrCodeValue} style={{ width: 280, height: 280, margin: '0 auto' }} />
              ) : (
                <div
                  style={{
                    width: 280,
                    height: 280,
                    margin: 'auto'
                  }}
                >
                  <Typography
                    textAlign={'center'}
                    sx={{ position: 'relative', top: '50%', transform: 'translateY(-50%)' }}
                  >
                    {qrCodeValue === undefined ? (
                      <CircularProgress size={'2rem'} color="inherit" />
                    ) : (
                      'QRコード読み込みエラー\n再ログインしてください'
                    )}
                  </Typography>
                </div>
              )}
            </Grid>
            <Grid mt={3} textAlign={'center'}>
              <Typography fontSize={18} className="font-bold">
                こちらのコードを
              </Typography>
              <Typography fontSize={18} className="font-bold">
                QRコードリーダーにかざしてください
              </Typography>
            </Grid>
            <Grid mt={2} textAlign={'center'}>
              <Typography fontSize={14}>うまく読み取れない場合は</Typography>
              <Typography fontSize={14}>スタッフにお申し付けください</Typography>
            </Grid>
          </Grid>
        </Box>
      </Layout>
    </>
  );
}
